import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { atom_tourStage } from '@app/shared/state/loading.atom';
import { atom_tourState } from '@app/shared/state/tour.atom';
import { atom_showModalContent, atom_modalType } from '@app/shared/state/show-content-modal.atom';

const STORAGE_KEY = 'dguide-accessibility-settings';

export const AccessibilityControls: React.FC<{
  position?: 'top-right' | 'bottom-right';
  sidebarStyle?: boolean;
  isDesktop?: boolean;
}> = ({
  position = 'top-right',
  sidebarStyle = false,
  isDesktop = true,
}) => {
  const { t } = useTranslation();
  const state_TourStage = useRecoilValue(atom_tourStage);
  const activeTour = useRecoilValue(atom_tourState);
  const modalType = useRecoilValue(atom_modalType);
  const setShowModal = useSetRecoilState(atom_showModalContent);
  const setModalType = useSetRecoilState(atom_modalType);

  const isOpen = modalType === 'accessibility';

  const handleOpen = () => {
    if (isOpen) {
      setShowModal(false);
      setModalType(null);
      return;
    }
    setModalType('accessibility');
    setShowModal(true);
  };

  const DEFAULT_TOUR_COLOR = '#D0552E';
  const tourColor = activeTour?.config?.mainColor || DEFAULT_TOUR_COLOR;

  if (sidebarStyle) {
    return (
      <Tooltip title={t('accessibility.accessibility_settings') || 'Accessibility'} placement={isDesktop ? 'right' :
'top'} arrow>
        <IconButton
          onClick={handleOpen}
          aria-label={t('accessibility.accessibility_settings') as string}
          aria-expanded={isOpen}
          aria-haspopup="true"
          sx={{
            position: 'relative',
            width: '100%',
            height: isDesktop ? 72 : 56,
            paddingTop: '10px',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: isOpen ? tourColor : '#666',
            bgcolor: 'transparent',
            transition: 'all 0.3s ease',
            '&:hover': { bgcolor: 'rgba(208, 85, 46, 0.08)' },
            '&::before': {
              content: '""',
              position: 'absolute',
              ...(isDesktop ? {
                left: -1, top: '50%', transform: 'translateY(-50%)',
                width: '4px', height: '70%', borderRadius: '0 4px 4px 0',
              } : {
                bottom: 0, left: '50%', transform: 'translateX(-50%)',
                width: '70%', height: '3px', borderRadius: '4px 4px 0 0',
              }),
              bgcolor: isOpen ? tourColor : 'transparent',
              transition: 'all 0.3s ease',
            },
          }}
        >
          <i
            className={isOpen ? "fa-solid fa-universal-access" : "fa-regular fa-universal-access"}
            aria-hidden="true"
            style={{ fontSize: isDesktop ? '22px' : '20px' }}
          />
          <Typography variant="caption" sx={{ mt: 0.5, fontSize: isDesktop ? '0.75rem' : '0.65rem' }}>
            {t('accessibility.title', 'Accessibility')}
          </Typography>
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box
      onClick={handleOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleOpen(); }}
      role="button"
      tabIndex={0}
      aria-label={t('accessibility.accessibility_settings') as string}
      aria-expanded={isOpen}
      aria-haspopup="true"
      sx={{
        display: 'flex',
        flexDirection: state_TourStage === 'map' ? 'column' : 'row',
        width: '100%', maxWidth: state_TourStage === 'map' ? '60px' : 'none', height: state_TourStage === 'map' ? '40px' : 'auto',
        alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.2s',
        ...(state_TourStage === 'map' ? { '&:hover': { backgroundColor: '#f5f5f5' } } : {}),
        gap: state_TourStage === 'map' ? 0 : 1,
      }}
    >
      <i className="fa-regular fa-universal-access" aria-hidden="true" style={{
        fontSize: state_TourStage === 'map' ? 22 : 20,
        color: state_TourStage === 'map' ? '#666' : 'inherit',
        marginRight: state_TourStage === 'map' ? 8 : 0,
        marginLeft: state_TourStage === 'map' ? 8 : 0,
        marginBottom: state_TourStage === 'map' ? 4 : 0,
      }}></i>
      {state_TourStage === 'map' ? (
        <Typography sx={{
          fontFamily: 'SF Pro Text', fontWeight: 550,
          fontSize: '14px', lineHeight: '16px',
          letterSpacing: '0px', textAlign: 'center',
        }}>
          {t('accessibility.accessibility_settings') as string}
        </Typography>
      ) : (
        t('accessibility.accessibility_settings') as string
      )}
    </Box>
  );
};

export const useAccessibilitySettings = () => {
  const [settings, setSettings] = useState<{ fontSize: string; highContrast: boolean; reducedMotion: boolean }>({
    fontSize: 'medium', highContrast: false, reducedMotion: false,
  });
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try { setSettings(JSON.parse(stored)); } catch (e) { console.error('Failed to load accessibility settings', e);
}
    }
  }, []);
  return settings;
};
